import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { SurfaceCard } from "components/ui/surface-card"
import { SectionHeader } from "components/ui/section-header"
import type { WidgetProps } from "lib/widget-types"
import { ChevronRight, Phone, Mail, Calendar } from "lucide-react"
import { getAppointments } from "services/appointments"

type InteractionType = "call" | "email" | "meeting"

interface Interaction {
  id: string
  type: InteractionType
  name: string
  subject: string
  time: string
}

const typeStyles = {
  call: {
    bgColor: "var(--hexa-info-light)",
    iconColor: "#00d3f2",
    Icon: Phone,
  },
  email: {
    bgColor: "var(--hexa-purple-light)",
    iconColor: "var(--hexa-purple)",
    Icon: Mail,
  },
  meeting: {
    bgColor: "var(--hexa-info-light)",
    iconColor: "var(--hexa-info)",
    Icon: Calendar,
  },
}

function getInteractionType(appointmentType?: string): InteractionType {
  if (appointmentType === "CALL" || appointmentType === "call") return "call"
  if (appointmentType === "EMAIL" || appointmentType === "email") return "email"
  return "meeting"
}

function formatInteractionTime(dateString?: string): string {
  if (!dateString) return "-"
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
  } else if (diffDays === 1) {
    return "Ieri"
  } else if (diffDays < 7) {
    return `${diffDays} giorni fa`
  } else {
    return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })
  }
}

export function LastInteractionsWidget({ widget }: WidgetProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [interactions, setInteractions] = useState<Interaction[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchInteractions = async () => {
      try {
        // Fetch recent appointments as interactions
        const response = await getAppointments({ past_only: true })
        const appointments = Array.isArray(response.data) ? response.data : []

        // Sort by date (most recent first) and take first 3
        const sortedAppointments = appointments
          .sort((a: any, b: any) => new Date(b.start_time || b.date).getTime() - new Date(a.start_time || a.date).getTime())
          .slice(0, 3)

        const formattedInteractions: Interaction[] = sortedAppointments.map((apt: any) => ({
          id: String(apt.id),
          type: getInteractionType(apt.appointment_type),
          name: apt.client_name || apt.title || "Interazione",
          subject: apt.description || apt.subject || "",
          time: formatInteractionTime(apt.start_time || apt.date),
        }))

        setInteractions(formattedInteractions)
      } catch (error) {
        console.error("Error fetching interactions:", error)
        setInteractions([])
      } finally {
        setLoading(false)
      }
    }

    fetchInteractions()
  }, [])

  return (
    <SurfaceCard className="h-full flex flex-col overflow-hidden">
      <Link to="/contacts" className="cursor-pointer">
        <SectionHeader title={widget.title || "Ultime interazioni"} />
      </Link>

      <div className="flex-1 min-h-0 flex flex-col space-y-2">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="text-sm" style={{ color: "var(--hexa-text-secondary)" }}>Caricamento...</span>
          </div>
        ) : interactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div
              className="flex items-center justify-center mb-2.5"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                backgroundColor: "var(--hexa-empty-icon-bg, rgba(123, 63, 255, 0.1))",
              }}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: "var(--hexa-empty-icon-color, #7b3fff)", opacity: "var(--hexa-empty-icon-opacity, 0.7)" }}
              >
                <path d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span
              className="text-center"
              style={{ fontSize: 11, color: "var(--hexa-empty-text-color, rgba(255,255,255,0.4))" }}
            >
              Nessuna interazione recente
            </span>
          </div>
        ) : (
          interactions.map((interaction) => {
            const style = typeStyles[interaction.type]
            const Icon = style.Icon
            const isHovered = hoveredId === interaction.id
            return (
              <Link
                key={interaction.id}
                to="/contacts"
                className="block w-full border rounded-lg p-2.5 transition-all group"
                style={{
                  backgroundColor: isHovered ? "var(--hexa-sidebar-item-hover)" : "var(--hexa-border-subtle)",
                  borderColor: isHovered ? "var(--hexa-border)" : "var(--hexa-border-subtle)",
                }}
                onMouseEnter={() => setHoveredId(interaction.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: style.bgColor }}
                  >
                    <Icon className="w-4 h-4" style={{ color: style.iconColor }} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-xs font-medium truncate" style={{ color: "var(--hexa-text-primary)" }}>
                      {interaction.name}
                    </div>
                    <div className="text-xs truncate mt-0.5" style={{ color: "var(--hexa-text-secondary)" }}>
                      {interaction.subject}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs tabular-nums" style={{ color: "var(--hexa-text-secondary)" }}>
                      {interaction.time}
                    </span>
                    <ChevronRight
                      className="w-3.5 h-3.5 transition-opacity"
                      style={{
                        color: "var(--hexa-purple)",
                        opacity: isHovered ? 1 : 0.6,
                      }}
                    />
                  </div>
                </div>
              </Link>
            )
          })
        )}
      </div>
    </SurfaceCard>
  )
}
